'use client';
import { WordDetails } from '@/api/words/getWordDetail';
import { Bookmark, Volume2, Heart, Share2, BookmarkCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import Toast from '@/components/Toast';
import { usePathname, useRouter } from 'next/navigation';
import {
  addBookmarkClient,
  deleteBookmarkClient,
  getBookmarkByWordClient,
} from '@/api/bookmarks/client';
import { links } from '@/utils/links';

interface WordHeaderButtonsProps {
  word: WordDetails;
}

export default function WordHeaderButtons({ word }: WordHeaderButtonsProps) {
  const runAudio = (_url: string) => {
    void _url; // placeholder for future playback
    // TODO: implement audio playback
  };
  const currentAudioIndex = 0;
  const pathname = usePathname();
  const router = useRouter();
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [bookmarkId, setBookmarkId] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<"success" | "error" | "info">("success");
  const [isBookmarkLoading, setIsBookmarkLoading] = useState(false);

  useEffect(() => {
    async function fetchStatus() {
      try {
        const { bookmark } = await getBookmarkByWordClient({ wordId: word.id });
        setIsBookmarked(Boolean(bookmark));
        setBookmarkId(bookmark?.id ?? null);
      } catch (err) {
        console.error('Failed to get bookmark status', err);
      }
    }
    fetchStatus();
  }, [word.id]);

  const toggleBookmark = async () => {
    if (isBookmarkLoading) return;
    
    setIsBookmarkLoading(true);
    try {
      if (isBookmarked) {
        if (!bookmarkId) return;
        await deleteBookmarkClient({ bookmarkId });
        setIsBookmarked(false);
        setBookmarkId(null);
        setToastMessage('Removed from bookmarks');
        setToastType('info');
      } else {
        const { bookmark } = await addBookmarkClient({ wordId: word.id });
        setIsBookmarked(true);
        setBookmarkId(bookmark.id);
        setToastMessage('Added to bookmarks');
        setToastType('success');
      }
      setTimeout(() => setToastMessage(null), 3000);
    } catch (err: unknown) {
      if ((err as { status?: number }).status === 401) {
        router.push(links.login({ returnTo: pathname }));
        return;
      }
      setToastMessage('Failed to update bookmark');
      setToastType('error');
      setTimeout(() => setToastMessage(null), 3000);
      console.error('Failed to toggle bookmark', err);
    } finally {
      setIsBookmarkLoading(false);
    }
  };

  const shareWord = async () => {
    try {
      const url = window.location.href;
      if (navigator.share) {
        await navigator.share({ title: word.name, url });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        setToastMessage('Link copied to clipboard!');
        setToastType('success');
        setTimeout(() => setToastMessage(null), 3000);
      }
    } catch (err) {
      console.error('Failed to share', err);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2 sm:gap-3">
        {word.audios.length > 0 && (
          <button
            onClick={() => runAudio(word.audios[currentAudioIndex].link)}
            className="group relative p-3 sm:p-4 rounded-full bg-gradient-to-br from-periwinkle to-periwinkle/80 text-white hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
            title="Play pronunciation"
          >
            <Volume2 className="h-5 w-5 sm:h-6 sm:w-6" />
            <div className="absolute -top-2 -right-2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
          </button>
        )}

        <button
          onClick={toggleBookmark}
          disabled={isBookmarkLoading}
          className={`group relative p-3 sm:p-4 rounded-full transition-all duration-300 transform hover:-translate-y-1 disabled:opacity-50 min-w-[48px] min-h-[48px] ${
            isBookmarked
              ? 'bg-gradient-to-br from-leaf-green to-leaf-green/80 text-white shadow-lg'
              : 'bg-white border-2 border-gray-200 text-gray-600 hover:border-leaf-green hover:text-leaf-green hover:shadow-lg'
          }`}
          title={isBookmarked ? 'Remove from bookmarks' : 'Add to bookmarks'}
        >
          {isBookmarkLoading ? (
            <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-current"></div>
          ) : isBookmarked ? (
            <>
              <BookmarkCheck className="h-5 w-5 sm:h-6 sm:w-6" />
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-leaf-green rounded-full border-2 border-white flex items-center justify-center">
                <div className="w-2 h-2 bg-white rounded-full"></div>
              </div>
            </>
          ) : (
            <Bookmark className="h-5 w-5 sm:h-6 sm:w-6" />
          )}
        </button>

        <button 
          className="p-3 sm:p-4 rounded-full bg-gradient-to-br from-coral-splash to-coral-splash/80 text-white hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
          title="Add to favorites"
        >
          <Heart className="h-5 w-5 sm:h-6 sm:w-6" />
        </button>

        <button
          onClick={shareWord}
          className="p-3 sm:p-4 rounded-full bg-gradient-to-br from-peach-sand to-peach-sand/80 text-white hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
          title="Share word"
        >
          <Share2 className="h-5 w-5 sm:h-6 sm:w-6" />
        </button>
      </div>
      {toastMessage && (
        <Toast 
          message={toastMessage} 
          type={toastType}
          onClose={() => setToastMessage(null)} 
        />
      )}
    </>
  );
}